import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Send, 
  Calendar, 
  Linkedin, 
  Instagram,
  ExternalLink,
  ArrowRight
} from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { cn, fadeInUp, staggerContainer, springPresets } from '@/lib/index';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const contactFormSchema = z.object({
  name: z.string().min(2, { message: 'Imię i nazwisko musi mieć co najmniej 2 znaki.' }),
  email: z.string().email({ message: 'Proszę podać poprawny adres e-mail.' }),
  subject: z.string().min(5, { message: 'Temat musi mieć co najmniej 5 znaków.' }),
  message: z.string().min(10, { message: 'Wiadomość musi mieć co najmniej 10 znaków.' }),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export default function Contact() {
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
    },
  });

  function onSubmit(values: ContactFormValues) {
    console.log(values);
    toast.success('Wiadomość została wysłana. Odpowiem najszybciej jak to możliwe.');
    form.reset();
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.MODERN_OFFICE_10} 
            alt="Gabinet Racheli Nowak" 
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/0 via-background/80 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.h1 
              variants={fadeInUp}
              className="text-5xl lg:text-7xl font-semibold tracking-tight text-foreground mb-6 font-[Cormorant_Garamond]"
            >
              Porozmawiajmy o <span className="text-accent">zmianie</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-muted-foreground leading-relaxed"
            >
              Zapraszam do kontaktu w celu umówienia konsultacji, mediacji lub nawiązania współpracy projektowej. 
              Każda wiadomość jest traktowana z pełną dyskrecją i uwagą.
            </motion.p>
            <motion.div variants={fadeInUp} className="mt-8">
              <Button asChild size="lg" className="rounded-full px-8">
                <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer">
                  Umów wizytę online <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
            
            {/* Contact Information Column */}
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="lg:col-span-5 space-y-12"
            >
              <div>
                <motion.h2 variants={fadeInUp} className="text-3xl font-semibold mb-8 font-[Cormorant_Garamond]">
                  Dane kontaktowe
                </motion.h2>
                <div className="space-y-6">
                  <motion.div variants={fadeInUp} className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-secondary text-primary">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Telefon</p>
                      <p className="text-muted-foreground">786967470</p>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeInUp} className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-secondary text-primary">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">E-mail</p>
                <p className="text-muted-foreground">gabinetrachelanowak@gmail.com</p>
                    </div>
                  </motion.div>


                </div>
              </div>

              <motion.div variants={fadeInUp}>
                <h3 className="text-xl font-semibold mb-6 font-[Cormorant_Garamond]">Godziny przyjęć</h3>
                <div className="space-y-3 bg-muted/50 p-6 rounded-2xl border border-border">
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 text-muted-foreground"><Clock className="w-4 h-4" /> Poniedziałek - Czwartek</span>
                    <span className="font-medium">09:00 - 20:00</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 text-muted-foreground"><Clock className="w-4 h-4" /> Piątek</span>
                    <span className="font-medium">09:00 - 16:00</span>
                  </div>
                  <div className="flex justify-between items-center text-muted-foreground/60">
                    <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> Sobota - Niedziela</span>
                    <span>Zamknięte</span>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="space-y-4">
                <h3 className="text-xl font-semibold font-[Cormorant_Garamond]">Szybka rezerwacja</h3>
                <Button 
                  variant="outline" 
                  className="w-full justify-between h-14 rounded-xl border-accent/20 hover:bg-accent/5 group"
                  onClick={() => window.open('https://znanylekarz.pl', '_blank')}
                >
                  <span className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-accent" />
                    Umów wizytę przez ZnanyLekarz
                  </span>
                  <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex gap-4">
                <a href="#" className="p-3 rounded-full bg-secondary text-primary hover:bg-primary hover:text-white transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="#" className="p-3 rounded-full bg-secondary text-primary hover:bg-primary hover:text-white transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
              </motion.div>
            </motion.div>

            {/* Form Column */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="lg:col-span-7"
            >
              <Card className="border-none shadow-2xl shadow-primary/5 bg-card overflow-hidden">
                <div className="bg-primary p-8 text-primary-foreground">
                  <h2 className="text-2xl font-semibold mb-2 font-[Cormorant_Garamond]">Napisz do mnie</h2>
                  <p className="opacity-80 text-sm">Zazwyczaj odpowiadam w ciągu 24 godzin roboczych.</p>
                </div>
                <CardContent className="p-8">
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Imię i nazwisko</FormLabel>
                              <FormControl>
                                <Input placeholder="Jan Kowalski" className="rounded-xl" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Adres e-mail</FormLabel>
                              <FormControl>
                                <Input placeholder="jan@przyklad.pl" className="rounded-xl" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="subject"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Temat</FormLabel>
                            <FormControl>
                              <Input placeholder="W czym mogę pomóc?" className="rounded-xl" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Wiadomość</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Opisz krótko swoją sytuację..." 
                                className="min-h-[160px] rounded-xl resize-none" 
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button 
                        type="submit" 
                        className="w-full h-14 text-lg rounded-xl shadow-lg shadow-primary/20 bg-primary hover:bg-primary/90 transition-all"
                      >
                        Wyślij wiadomość
                        <Send className="ml-2 w-5 h-5" />
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>

              <div className="mt-12 text-center p-8 border border-dashed border-border rounded-2xl bg-muted/30">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Pamiętaj, że kontakt mailowy nie służy do udzielania pomocy w stanach nagłego zagrożenia życia lub zdrowia. 
                  W sytuacjach kryzysowych prosimy o kontakt z numerem alarmowym <span className="font-semibold text-foreground">112</span> lub udanie się do najbliższego szpitala.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Simple Map / Visual Anchor */}
      <section className="pb-24">
        <div className="container mx-auto px-4">
          <div className="relative w-full h-[400px] rounded-3xl overflow-hidden bg-muted">
            <div className="absolute inset-0 grayscale contrast-125 opacity-50">
              <img 
                src={IMAGES.MODERN_OFFICE_10} 
                alt="Lokalizacja gabinetu" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-background/90 backdrop-blur-md p-8 rounded-2xl shadow-xl border border-border text-center max-w-sm mx-4">
                <MapPin className="w-10 h-10 text-accent mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Kalisz, Warszawa</h3>
                <p className="text-muted-foreground text-sm mb-4">Centrum Warszawy, doskonały dojazd komunikacją miejską oraz dostępny parking.</p>
                <Button variant="link" className="text-accent p-0 h-auto font-medium">
                  Otwórz w Mapach Google
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
